import { useMemo, useEffect, useState, useCallback } from "react";
import {
  RevActionsHeader,
  RevAvatar,
  RevButton,
  RevDialog,
  type RevDialogFooterBtnListType,
  RevIconButton,
  RevSpinner,
  RevTable,
  RevSlideout,
  RevTooltip,
} from "@revmatics/catalyst";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { faEye, faUserPlus } from "@fortawesome/pro-light-svg-icons";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks";
import {
  fetchPersonaGroups,
  deletePersonaGroup,
} from "../../stores/thunks/personaGroupsThunk.tsx";
import { setSelectedGeneration } from "../../stores/slice/personaGroupsSlice.tsx";
import { personaGroupType } from "../../stores/types/personaGroupsTypes.tsx";
import { PersonaGroupDetailView } from "./components/personaGroups/PersonaGroupDetailView.tsx";
import { PersonaSelectorDialog } from "./components/personaGroups/PersonaSelectorDialog.tsx";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";

const columnHelper = createColumnHelper<personaGroupType>();

export const PersonaGroupsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const personaGroups = useAppSelector(
    (state) => state.personaGroups.personaGroups,
  );
  const isLoading = useAppSelector((state) => state.personaGroups.isLoading);
  const selectedGeneration = useAppSelector(
    (state) => state.personaGroups.selectedGeneration,
  );

  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [isPersonaSelectorOpen, setIsPersonaSelectorOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [generationToDelete, setGenerationToDelete] =
    useState<personaGroupType | null>(null);

  useEffect(() => {
    if (!personaGroups) {
      dispatch(fetchPersonaGroups());
    }
  }, [dispatch, personaGroups]);

  const handleOpenDetail = useCallback(
    (group: personaGroupType) => {
      dispatch(setSelectedGeneration(group));
      setIsSlideoutOpen(true);
    },
    [dispatch],
  );

  const handleCloseSlideout = () => {
    setIsSlideoutOpen(false);
    dispatch(setSelectedGeneration(null));
  };

  const handleTriggerAddFromSlideout = () => {
    setIsSlideoutOpen(false);
    // Small timeout to allow slideout transition to clear before showing dialog
    setTimeout(() => {
      setIsPersonaSelectorOpen(true);
    }, 200);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setTimeout(() => setGenerationToDelete(null), 150);
  };

  const handleConfirmDelete = () => {
    if (!generationToDelete) return;
    dispatch(deletePersonaGroup(generationToDelete.id))
      .unwrap()
      .finally(() => handleCloseDeleteDialog());
  };

  const deleteDialogActions: RevDialogFooterBtnListType = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={handleCloseDeleteDialog}
    />,
    <RevButton
      key="delete"
      text="Delete Group"
      variant="primary"
      buttonColor="error"
      handleClick={handleConfirmDelete}
      isLoading={isLoading}
    />,
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<personaGroupType, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Name
          </span>
        ),
        cell: (info) => (
          <div className="flex items-center gap-3">
            <RevAvatar
              variant="circle"
              size="medium"
              theme="light"
              firstName={info.getValue()}
              gender="male"
            />
            <RevButton
              displayAs="link"
              text={info.getValue()}
              linkFontSize="small"
              handleClick={() => handleOpenDetail(info.row.original)}
            />
          </div>
        ),
        enableSorting: true,
        size: 200,
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Description
          </span>
        ),
        cell: (info) => (
          <div className="line-clamp-2 typography-body-sm text-monochrome-950">
            {info.getValue() || "-"}
          </div>
        ),
        enableSorting: true,
        size: 250,
      }),
      columnHelper.accessor("persona_count", {
        id: "persona_count",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs w-full block">
            Personas
          </span>
        ),
        cell: (info) => {
          const count = info.getValue();
          if (count === 0) {
            return (
              <div className="flex justify-left">
                <RevButton
                  text="Add Personas"
                  buttonSize="extraSmall"
                  variant="secondary"
                  handleClick={() => {
                    dispatch(setSelectedGeneration(info.row.original));
                    setIsPersonaSelectorOpen(true);
                  }}
                />
              </div>
            );
          }
          return (
            <div className="typography-body-sm text-monochrome-950">
              {count}
            </div>
          );
        },
        enableSorting: true,
        size: 250,
      }),
      columnHelper.accessor("updated_at", {
        id: "updated_at",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs">
            Last Updated
          </span>
        ),
        cell: (info) => formatDateToMonthDayYear(info.getValue() ?? ""),
        enableSorting: true,
        size: 150,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-center w-full block">
            Actions
          </span>
        ),
        cell: (props) => (
          <div className="flex items-center gap-1 justify-center">
            <RevTooltip
              position="left"
              className="w-[160px]"
              content="View / Manage Generation"
            >
              <RevIconButton
                icon={faEye}
                size="m"
                variant="default"
                iconSize="small"
                handleClick={() => handleOpenDetail(props.row.original)}
              />
            </RevTooltip>

            <RevTooltip
              position="left"
              className="w-[95px]"
              content="Add Personas"
            >
              <RevIconButton
                icon={faUserPlus}
                size="m"
                variant="default"
                iconSize="small"
                handleClick={() => {
                  dispatch(setSelectedGeneration(props.row.original));
                  setIsPersonaSelectorOpen(true);
                }}
              />
            </RevTooltip>
          </div>
        ),
        size: 80,
      }),
    ],
    [handleOpenDetail, dispatch],
  );

  return (
    <div>
      <RevActionsHeader title="Generations" />

      {isLoading && (!personaGroups || personaGroups.length === 0) ? (
        <div className="h-full flex items-center justify-center p-8">
          <RevSpinner size="large" color="primary" centered />
        </div>
      ) : (
        <RevTable<personaGroupType>
          data={personaGroups || []}
          columns={columns}
          initialPageSize={10}
          enableGlobalFilter={false}
          enablePagination={true}
        />
      )}

      <RevSlideout
        isOpen={isSlideoutOpen}
        setIsOpen={handleCloseSlideout}
        title={selectedGeneration?.name}
        subtitle={selectedGeneration?.description}
        size="md"
      >
        {selectedGeneration && (
          <PersonaGroupDetailView
            generation={selectedGeneration}
            onClose={handleCloseSlideout}
            onTriggerAdd={handleTriggerAddFromSlideout}
          />
        )}
      </RevSlideout>

      {selectedGeneration && (
        <PersonaSelectorDialog
          isOpen={isPersonaSelectorOpen}
          setIsOpen={setIsPersonaSelectorOpen}
          group={selectedGeneration}
          onSuccess={() => dispatch(fetchPersonaGroups())}
        />
      )}

      {generationToDelete && (
        <RevDialog
          isOpen={isDeleteDialogOpen}
          setIsOpen={setIsDeleteDialogOpen}
          title="Delete Group"
          btnList={deleteDialogActions}
        >
          <p className="typography-body-sm text-monochrome-900">
            Are you sure you want to delete {generationToDelete.name}?
          </p>
        </RevDialog>
      )}
    </div>
  );
};
